// KioskLinePhoto — thumbnail-then-full-image display for a per-line capture
// (gross/tare/material). Mirrors WbSnapshotThumb's presign-by-key pattern
// (used on the main ticket page) but with a narrower prop shape: a per-line
// capture is either a raw storage key (from the ticket-detail fetch — needs
// presigning) or an already-resolved direct URL (a live ticket_snapshot_saved
// SSE event, which the server presigns before broadcast — used as-is, no
// extra request). Built as its own small component rather than loosening
// WbSnapshotThumb's contract, since the two call sites have genuinely
// different data shapes.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import styles from './kiosk.module.css';

interface PresignedResponse {
  key: string;
  url: string;
  expires_at: string;
}

export interface KioskLinePhotoProps {
  /** Scope key (ticket enc_id) — namespaces the presign query cache entry. */
  scopeKey: string;
  /** Caption suffix, e.g. "Gross", "Tare", "Material". */
  label: string;
  /** Already-resolved, directly renderable URL — used as-is, no presign call. */
  directUrl?: string;
  /** Raw storage key — presigned via /files/presigned when directUrl isn't given. */
  storageKey?: string;
  /** True while a fresh capture for this slot is in flight. Takes priority
   *  over any existing photo — a recapture shows the spinner, not the stale
   *  image, so it's clear a new one is on the way. */
  capturing?: boolean;
}

export function KioskLinePhoto({
  scopeKey,
  label,
  directUrl,
  storageKey,
  capturing = false,
}: KioskLinePhotoProps): JSX.Element | null {
  const presignQ = useQuery<{ url: string }>({
    queryKey: ['kiosk', 'line-photo', scopeKey, storageKey],
    queryFn: async () => {
      const res = await apiClient.get<PresignedResponse>('/files/presigned', {
        params: { key: storageKey },
      });
      return { url: res.data.url };
    },
    enabled: directUrl === undefined && storageKey !== undefined,
    staleTime: 30_000,
    retry: false,
  });

  if (capturing) {
    return (
      <span className={styles.linePhotoPlaceholder}>
        <Spinner size="sm" label="Capturing photo" />
      </span>
    );
  }

  if (directUrl === undefined && storageKey === undefined) return null;

  const url = directUrl ?? presignQ.data?.url;
  const alt = `${label} capture`;

  if (url === undefined) {
    if (presignQ.isError) {
      return <span className={styles.linePhotoPlaceholder}>Image unavailable</span>;
    }
    return (
      <span className={styles.linePhotoPlaceholder}>
        <Spinner size="sm" label="Loading image" />
      </span>
    );
  }

  return (
    <a className={styles.linePhotoLink} href={url} target="_blank" rel="noopener noreferrer">
      <img className={styles.weightPhoto} src={url} alt={alt} loading="lazy" />
    </a>
  );
}
